// src/subscreens/superadmin/reports/ReportsPage.jsx

import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FiMoreVertical, FiDownload, FiCalendar, FiDollarSign, FiShoppingBag, FiTool, FiUserPlus } from 'react-icons/fi';
import AppPagination from "../../../components/general/pagination/Pagination.js";
import '../../../assets/css/superadmin/reportsanalytics/ReportsAnalytics.css';
import MasterLayout from '../../../layout/superadmin/MasterLayout.js';

const ReportsPage = () => {

  // ── ReportsHeader States ──
  const [dateRange, setDateRange] = useState('last30days');

  // ── TopPerformingEquipments Pagination State ──
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // ── ReportsCharts Data ──
  const revenueData = [
    { month: 'JAN', revenue: 95000,  maintenance: 25000 },
    { month: 'FEB', revenue: 110000, maintenance: 28000 },
    { month: 'MAR', revenue: 85000,  maintenance: 32000 },
    { month: 'APR', revenue: 105000, maintenance: 30000 },
    { month: 'MAY', revenue: 130000, maintenance: 35000 },
    { month: 'JUN', revenue: 120000, maintenance: 33000 },
  ];

  const utilizationData = { active: 85, rented: 65, idle: 20, maintenance: 15 };

  // ── ReportsStats Data ──
  const stats = [
    { title: 'Total Revenue',       value: '$1.2M', change: '+12%', icon: <FiDollarSign size={24} />, iconColor: '#E5A512', changeType: 'positive' },
    { title: 'Active Rentals',      value: '342',   change: '+5%',  icon: <FiShoppingBag size={24} />, iconColor: '#E5A512', changeType: 'positive' },
    { title: 'Pending Maintenance', value: '18',    change: '-2%',  icon: <FiTool size={24} />,        iconColor: '#F44336', changeType: 'negative' },
    { title: 'New Owners',          value: '+45',   change: '+8%',  icon: <FiUserPlus size={24} />,    iconColor: '#4CAF50', changeType: 'positive' },
  ];

  // ── TopPerformingEquipments Data ──
  const [equipment] = useState([
    { id: 1, name: 'John Deere 8R',    image: '🚜', category: 'Tractors',   owner: 'AgriTech Solutions', totalRentals: 45, revenue: '$12,450', status: 'available'   },
    { id: 2, name: 'Case IH Combine',  image: '🌾', category: 'Harvesters', owner: 'Midwest Farming',    totalRentals: 28, revenue: '$18,200', status: 'rented'      },
    { id: 3, name: 'Kubota Excavator', image: '🏗️', category: 'Excavators', owner: 'Green Earth Co',     totalRentals: 12, revenue: '$5,600',  status: 'maintenance' },
  ]);

  // ── ReportsHeader Data ──
  const dateRanges = [
    { value: 'last7days',  label: 'Last 7 Days'  },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'thismonth',  label: 'This Month'   },
    { value: 'lastmonth',  label: 'Last Month'   },
    { value: 'thisyear',   label: 'This Year'    },
  ];

  // ── Helpers ──
  const handleExportReport = () => console.log('Export Report clicked');
  const handleMenuClick    = () => console.log('Chart menu clicked');

  const getStatusBadge = (status) => {
    const config = {
      available:   { label: 'Available',   class: 'status-available'   },
      rented:      { label: 'Rented',      class: 'status-rented'      },
      maintenance: { label: 'Maintenance', class: 'status-maintenance' },
    };
    return config[status] || config.available;
  };

  // ── Pagination Logic ──
  const totalPages       = Math.ceil(equipment.length / itemsPerPage);
  const paginatedEquipment = equipment.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // ================================================================
  // RETURN
  // ================================================================
  return (
    <MasterLayout>
    <div>

      {/* ════════════════════════════════
          REPORTS HEADER
      ════════════════════════════════ */}
      <div className="rep-header">
        <div className="rep-header-content">
          <div className="rep-header-text">
            <h1 className="rep-title">Reports & Analytics</h1>
            <p className="rep-subtitle">Monitor system performance, financial health, and equipment utilization across the ecosystem.</p>
          </div>
          <div className="rep-header-actions">
            <div className="rep-date-range">
              <label className="date-label">DATE RANGE</label>
              <div className="date-select-wrapper">
                <FiCalendar className="date-icon" />
                <select className="date-select" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                  {dateRanges.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <button className="rep-export-btn" onClick={handleExportReport}>
              <FiDownload size={18} />
              <span>Export Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════
          REPORTS STATS
      ════════════════════════════════ */}
      <div className="rep-stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="rep-stat-card">
            <div className="rep-stat-content">
              <div className="rep-stat-icon" style={{ color: stat.iconColor }}>
                {stat.icon}
              </div>
              <div className="rep-stat-text">
                <p className="rep-stat-title">{stat.title}</p>
                <h2 className="rep-stat-value">{stat.value}</h2>
                <span className={`rep-stat-change ${stat.changeType}`}>{stat.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ════════════════════════════════
          REPORTS CHARTS
      ════════════════════════════════ */}
      <div className="rep-charts-grid">

        {/* Revenue vs Maintenance */}
        <div className="rep-chart-card">
          <div className="chart-header">
            <div className="chart-title-section">
              <h3 className="chart-title">Revenue vs Maintenance Costs</h3>
              <p className="chart-subtitle">Financial performance over the last 6 months</p>
            </div>
            <button className="chart-menu-btn" onClick={handleMenuClick}>
              <FiMoreVertical size={20} />
            </button>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                <XAxis dataKey="month" stroke="#999" style={{ fontSize: '12px' }} />
                <YAxis stroke="#999" style={{ fontSize: '12px' }} tickFormatter={(v) => `$${v / 1000}k`} />
                <Tooltip
                  formatter={(v) => `$${v.toLocaleString()}`}
                  contentStyle={{ background: '#FFF', border: '1px solid #E5E5E5', borderRadius: '8px', fontSize: '12px' }}
                />
                <Line type="monotone" dataKey="revenue"     stroke="#E5A512" strokeWidth={3} name="Revenue"      dot={{ fill: '#E5A512', r: 4 }} />
                <Line type="monotone" dataKey="maintenance" stroke="#999"    strokeWidth={2} name="Maint. Costs" dot={{ fill: '#999', r: 3 }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              <div className="legend-item"><span className="legend-dot revenue"></span><span className="legend-label">Revenue</span></div>
              <div className="legend-item"><span className="legend-dot maintenance"></span><span className="legend-label">Maint. Costs</span></div>
            </div>
          </div>
        </div>

        {/* Equipment Utilization */}
        <div className="rep-chart-card utilization-card">
          <div className="chart-header">
            <div className="chart-title-section">
              <h3 className="chart-title">Equipment Utilization</h3>
              <p className="chart-subtitle">Current status of all registered machinery</p>
            </div>
          </div>
          <div className="utilization-container">
            <div className="donut-chart">
              <svg viewBox="0 0 200 200" className="donut-svg">
                <circle cx="100" cy="100" r="70" fill="none" stroke="#E5E5E5" strokeWidth="20" />
                <circle cx="100" cy="100" r="70" fill="none" stroke="#E5A512" strokeWidth="20"
                  strokeDasharray={`${utilizationData.active * 4.4} 440`}
                  strokeDashoffset="0" transform="rotate(-90 100 100)" strokeLinecap="round" />
                <circle cx="100" cy="100" r="70" fill="none" stroke="#F44336" strokeWidth="20"
                  strokeDasharray={`${utilizationData.maintenance * 4.4} 440`}
                  strokeDashoffset={`-${utilizationData.active * 4.4}`}
                  transform="rotate(-90 100 100)" strokeLinecap="round" />
                <text x="100" y="95"  textAnchor="middle" className="donut-percentage">{utilizationData.active}%</text>
                <text x="100" y="115" textAnchor="middle" className="donut-label">Active</text>
              </svg>
            </div>
            <div className="utilization-stats">
              <div className="util-stat-item">
                <div className="util-stat-header"><span className="util-dot rented"></span><span className="util-label">Rented</span></div>
                <div className="util-value">{utilizationData.rented}%</div>
                <div className="util-sublabel">Currently in use</div>
              </div>
              <div className="util-stat-item">
                <div className="util-stat-header"><span className="util-dot idle"></span><span className="util-label">Idle</span></div>
                <div className="util-value">{utilizationData.idle}%</div>
                <div className="util-sublabel">Ready to rent</div>
              </div>
              <div className="util-stat-item">
                <div className="util-stat-header"><span className="util-dot maintenance"></span><span className="util-label">Maintenance</span></div>
                <div className="util-value">{utilizationData.maintenance}%</div>
                <div className="util-sublabel">Under repair</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════
          TOP PERFORMING EQUIPMENT
      ════════════════════════════════ */}
      <div className="rep-equipment-container">
        <div className="equipment-header">
          <div className="equipment-title-section">
            <h3 className="equipment-title">Top Performing Equipment</h3>
            <p className="equipment-subtitle">Highest revenue generating assets this month</p>
          </div>
        </div>
        <div className="equipment-table-wrapper">
          <table className="equipment-table">
            <thead>
              <tr>
                <th>Equipment Name</th>
                <th>Category</th>
                <th>Owner</th>
                <th>Total Rentals</th>
                <th>Revenue</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEquipment.map((item) => {
                const statusBadge = getStatusBadge(item.status);
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="equipment-name-cell">
                        <div className="equipment-image">{item.image}</div>
                        <span className="equipment-name">{item.name}</span>
                      </div>
                    </td>
                    <td><span className="equipment-category">{item.category}</span></td>
                    <td><span className="equipment-owner">{item.owner}</span></td>
                    <td><span className="equipment-rentals">{item.totalRentals}</span></td>
                    <td><span className="equipment-revenue">{item.revenue}</span></td>
                    <td><span className={`equipment-status ${statusBadge.class}`}>{statusBadge.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <AppPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={equipment.length}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

    </div>
    </MasterLayout>
  );
};

export default ReportsPage;